import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetSchema } from "./assetsBase";

export const orificeListItemSchema = baseAssetSchema.extend({
  geometry: lineStringSchema,
  name: z.string(),
  flow_type: z.string(),
  start_point: z.string().nullable() /* URL to Manhole */,
  end_point: z.string().nullable() /* URL to Manhole */,
  connection_serial: z.number().nullable(),
  angle: z.number().nullable(),
  crest_width: z.number().nullable(),
  crest_level: z.number().nullable(),
  shape: z.string(),
  initial_opening_height: z.number().nullable(),
  contraction_coeff: z.number().nullable(),
  lat_contr_coeff: z.number().nullable(),
  allowed_flow_direction: z.string().nullable(),
  negative_flow_limit: z.number().nullable(),
  positive_flow_limit: z.number().nullable(),
});

export type OrificeListItem = z.infer<typeof orificeListItemSchema>;

export const orificeListSchema = paginatedSchema(orificeListItemSchema);
export type OrificeList = z.infer<typeof orificeListSchema>;

export const orificeSchema = orificeListItemSchema;
export type Orifice = z.infer<typeof orificeSchema>;

export function useOrifices(params = {}) {
  const url = combineUrlAndParams("/api/v4/orifices/", params);
  return useParsedQuery<OrificeList>(
    ["lizard-api-v4", "orifices", url],
    () => fetchWithError(url),
    (json: unknown) => orificeListSchema.parse(json)
  );
}

export function useOrifice(id: number) {
  return useParsedQuery<Orifice>(
    ["lizard-api-v4", "orifices", id],
    () => fetchWithError(`/api/v4/orifices/${id}/`),
    (json: unknown) => orificeSchema.parse(json)
  );
}

export function useMultipleOrifices(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "orifices", id],
        fetchFunction: () => fetchWithError(`/api/v4/orifices/${id}/`),
        parseFunction: (json: unknown) => orificeSchema.parse(json),
      };
    })
  );
}
